import { useEffect, useState } from 'react';

export type ToastTone = 'error' | 'success' | 'info';
export interface ToastItem {
  id: number;
  message: string;
  tone: ToastTone;
  title?: string;
}

// Module-level pub/sub so non-React code (the fetch interceptor) can emit toasts
// without a hook. The Toaster component subscribes and renders them.
type Listener = (t: ToastItem) => void;
const listeners = new Set<Listener>();
let seq = 0;

/** Emit a toast from anywhere — React or not (e.g. the global fetch interceptor). */
export function pushToast(message: string, opts: { tone?: ToastTone; title?: string } = {}): void {
  const item: ToastItem = { id: ++seq, message, tone: opts.tone ?? 'error', title: opts.title };
  for (const l of listeners) l(item);
}

/** Hook for React call sites that want to toast directly (e.g. a form success). */
export function useToast(): typeof pushToast {
  return pushToast;
}

const AUTO_DISMISS_MS = 6000;
const MAX_VISIBLE = 4;

const TONE: Record<ToastTone, { wrap: string; icon: string; iconPath: string }> = {
  error: {
    wrap: 'border-rose-200 bg-rose-50',
    icon: 'text-rose-600',
    iconPath:
      'M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z',
  },
  success: {
    wrap: 'border-emerald-200 bg-emerald-50',
    icon: 'text-emerald-600',
    iconPath: 'M22 11.08V12a10 10 0 11-5.93-9.14M22 4L12 14.01l-3-3',
  },
  info: {
    wrap: 'border-sky-200 bg-sky-50',
    icon: 'text-sky-600',
    iconPath: 'M12 16v-4m0-4h.01M22 12a10 10 0 11-20 0 10 10 0 0120 0z',
  },
};

/** Fixed, auto-dismissing toast stack. Mount once near the app root. */
export function Toaster() {
  const [items, setItems] = useState<ToastItem[]>([]);

  useEffect(() => {
    const onToast: Listener = (t) => {
      setItems((cur) => [...cur, t].slice(-MAX_VISIBLE));
      window.setTimeout(() => setItems((cur) => cur.filter((x) => x.id !== t.id)), AUTO_DISMISS_MS);
    };
    listeners.add(onToast);
    return () => {
      listeners.delete(onToast);
    };
  }, []);

  const dismiss = (id: number) => setItems((cur) => cur.filter((x) => x.id !== id));
  if (items.length === 0) return null;

  return (
    <div className="pointer-events-none fixed right-4 top-4 z-[100] flex w-[22rem] max-w-[calc(100vw-2rem)] flex-col gap-2">
      {items.map((t) => {
        const tone = TONE[t.tone];
        return (
          <div
            key={t.id}
            role="alert"
            className={`pointer-events-auto flex items-start gap-2.5 rounded-xl border ${tone.wrap} px-3.5 py-3 shadow-lg`}
          >
            <svg
              className={`mt-0.5 h-5 w-5 shrink-0 ${tone.icon}`}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d={tone.iconPath} />
            </svg>
            <div className="min-w-0 flex-1">
              {t.title && <p className="text-sm font-semibold text-neutral-900">{t.title}</p>}
              <p className="break-words text-sm text-neutral-700">{t.message}</p>
            </div>
            <button
              type="button"
              onClick={() => dismiss(t.id)}
              aria-label="Dismiss"
              className="-mr-1 shrink-0 rounded p-0.5 text-neutral-400 hover:text-neutral-700"
            >
              <svg
                className="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        );
      })}
    </div>
  );
}
